import React, { FC, useLayoutEffect, useState } from 'react'
import cx from 'classnames'
import { debounce } from 'lodash'
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import { Responsive, WidthProvider } from '@goodrequest/react_grid_layout'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { IElementItem, IFormItemData, IGridItem, ITabBoxLayoutRef } from '../../../types/interfaces'
import TabElement from './TabsElement'
import { FORM_EDITED, LOAD } from '../../../reducers/form/formTypes'

// utils
import { patchReq } from '../../../utils/request'
import { DROPPING_ELEMENT, ELEMENT_TYPES, LAYOUT_ROW_HEIGHT, PAGE, PROTECTION_INTERVAL_FOR_SAVING_ELEMENT } from '../../../utils/enums'
import { getApiEndpointPath } from '../../../utils/helper'

// assets
import { ReactComponent as HeadingIcon } from '../../../assets/icons/ElementIcons/heading-icon.svg'
import { ReactComponent as CheckBoxIcon } from '../../../assets/icons/ElementIcons/checkbox-icon.svg'
import { ReactComponent as DropDownIcon } from '../../../assets/icons/ElementIcons/dropdown-icon.svg'
import { ReactComponent as InputIcon } from '../../../assets/icons/ElementIcons/input-icon.svg'
import { ReactComponent as RadioIcon } from '../../../assets/icons/ElementIcons/radio-icon.svg'
import { ReactComponent as TextareaIcon } from '../../../assets/icons/ElementIcons/textarea-icon.svg'
import { ReactComponent as ButtonIcon } from '../../../assets/icons/ElementIcons/button-icon.svg'
import { ReactComponent as TextIcon } from '../../../assets/icons/ElementIcons/text-icon.svg'
import { ReactComponent as TableIcon } from '../../../assets/icons/ElementIcons/table-icon.svg'
import { ReactComponent as HorizontalLineIcon } from '../../../assets/icons/ElementIcons/horizontal-line-icon.svg'
import { ReactComponent as DateIcon } from '../../../assets/icons/ElementIcons/date-icon.svg'
import { ReactComponent as CalendarIcon } from '../../../assets/icons/ElementIcons/calendar-icon.svg'
import { ReactComponent as TabIcon } from '../../../assets/icons/ElementIcons/section-icon.svg'
import { ReactComponent as ImageIcon } from '../../../assets/icons/ElementIcons/image-icon.svg'
import { ReactComponent as SelectFromTableIcon } from '../../../assets/icons/ElementIcons/webview-icon.svg'

// reducers
import { RootState } from '../../../reducers'

const ResponsiveReactGridLayout = WidthProvider(Responsive)

type Props = {
	formId?: string
	layouts: IGridItem[]
	items: IElementItem
	selectedItem: string
	selectedTabItem: string
	selectedNestedTabItem: string
	openItem: Function
	onLayoutChange: any
	onNewItemAdded: Function
	onAddNewTab: Function
	onRemoveTab: Function
	onClickSwitch: Function
	openItemInTabsElement: Function
	onLayoutChangeInTabsElement: Function
	onNewItemAddedToTabsElement: Function
	tabBoxLayoutReferences: ITabBoxLayoutRef
	resizeElement: Function
	reload: Function
	nestedElementsRendered: Function
	onRender?: Function
	page?: PAGE
}

const MainBuilder: FC<Props> = (props) => {
	const {
		layouts,
		items,
		selectedItem,
		openItem,
		onLayoutChange,
		onClickSwitch,
		onAddNewTab,
		onRemoveTab,
		openItemInTabsElement,
		onLayoutChangeInTabsElement,
		onNewItemAddedToTabsElement,
		tabBoxLayoutReferences,
		resizeElement,
		onRender,
		formId,
		reload,
		selectedTabItem,
		page,
		nestedElementsRendered,
		selectedNestedTabItem
	} = props
	const dispatch = useDispatch()
	const { t } = useTranslation()
	const [mainContainerWidth, setMainContainerWidth] = useState<number>(0)
	const [removePlaceholder, setRemovePlaceholder] = useState<boolean>(false)
	const disable = useSelector((state: RootState) => state.formTemplate.disable)
	const templateDisable = useSelector((state: RootState) => state.template.disable)
	const placeholderHeight = useSelector((state: RootState) => state.formTemplate.placeholder.height)
	const placeholderWidth = useSelector((state: RootState) => state.formTemplate.placeholder.width)

	const renderIcon = (type: string) => {
		switch (type) {
			case ELEMENT_TYPES.TEXT:
				return <TextIcon width={20} height={20} />
			case ELEMENT_TYPES.INPUT:
				return <InputIcon width={20} height={20} />
			case ELEMENT_TYPES.DROPDOWN_BUTTON:
				return <DropDownIcon width={20} height={20} />
			case ELEMENT_TYPES.CHECK_BOX:
				return <CheckBoxIcon width={20} height={20} />
			case ELEMENT_TYPES.RADIO_BUTTON:
				return <RadioIcon width={20} height={20} />
			case ELEMENT_TYPES.TEXT_AREA:
				return <TextareaIcon width={20} height={20} />
			case ELEMENT_TYPES.HORIZONTAL_LINE:
				return <HorizontalLineIcon width={20} height={20} />
			case ELEMENT_TYPES.DATE_PICKER:
				return <DateIcon width={20} height={20} />
			case ELEMENT_TYPES.CALENDAR:
				return <CalendarIcon width={20} height={20} />
			case ELEMENT_TYPES.BUTTON:
				return <ButtonIcon width={20} height={20} />
			case ELEMENT_TYPES.TABLE:
				return <TableIcon width={20} height={20} />
			case ELEMENT_TYPES.TITLE:
				return <HeadingIcon width={20} height={20} />
			case ELEMENT_TYPES.TAB_BOX:
				return <TabIcon width={20} height={20} />
			case ELEMENT_TYPES.IMAGE:
				return <ImageIcon width={20} height={20} className={'icon--stroked'} />
			case ELEMENT_TYPES.SELECT_FROM_TABLE:
				return <SelectFromTableIcon width={20} height={20} />
			default:
				return undefined
		}
	}

	/*
		when user drop new item to grid layout this function
		send context data which type of element item is dropped to grid layout
	*/
	const onDrop = (layout: IGridItem[], layoutItem: IGridItem, event: any) => {
		try {
			props.onNewItemAdded(JSON.parse(event.dataTransfer.getData('dropItem')), layoutItem)
		} catch (err: any) {
			/* eslint-disable-next-line no-console */
			console.error(err.message)
		}
	}

	// check if page is equals to template builder and if some template or form is editing
	const checkPage = () => {
		if (page === PAGE.TEMPLATE_BUILDER) {
			return !templateDisable
		}
		return !disable
	}

	const renderLayoutElements = () =>
		layouts.map((gridItem: IGridItem) => {
			const editClass = cx({
				'opened-element': selectedItem === gridItem.i,
				'tab-box layout-border': items[`${gridItem.i}`]?.type === ELEMENT_TYPES.TAB_BOX
			})

			const formAttributes = items[`${gridItem.i}`]?.formItemGeneral

			const renderLabel = (label: string, type: string) => {
				if (type === ELEMENT_TYPES.HORIZONTAL_LINE) {
					return t('loc:Horizontal line')
				}
				if (type === ELEMENT_TYPES.IMAGE) {
					return t('loc:Image')
				}
				return label
			}

			// set remove placeholder and trigger removing in main builder layout
			const onDragStartInTab = () => {
				setRemovePlaceholder(true)
			}

			return (
				<div
					onClick={() => {
						openItem(gridItem)
					}}
					id={`${gridItem.i}`}
					className={editClass}
					key={gridItem.i}
				>
					{items[`${gridItem.i}`]?.type === ELEMENT_TYPES.TAB_BOX && items[`${gridItem.i}`]?.tabs ? (
						<TabElement
							formId={formId}
							itemX={gridItem.x}
							itemY={gridItem.y}
							tabs={items[`${gridItem.i}`]?.tabs}
							itemKey={gridItem.i}
							locked={gridItem.static || false}
							onClickSwitch={onClickSwitch}
							onAddNewTab={onAddNewTab}
							onRemoveTab={onRemoveTab}
							selectedItem={selectedTabItem}
							openItem={openItemInTabsElement}
							onLayoutChange={onLayoutChangeInTabsElement}
							onNewItemAdded={onNewItemAddedToTabsElement}
							layoutHeight={tabBoxLayoutReferences[`${gridItem.i}`]?.height}
							onRender={onRender}
							selectedNestedTabItem={selectedNestedTabItem}
							reload={reload}
							resizeElement={resizeElement}
							page={page}
							mainContainerWidth={mainContainerWidth}
							selectedTabItem={selectedTabItem}
							openItemInTabsElement={openItemInTabsElement}
							onLayoutChangeInTabsElement={onLayoutChangeInTabsElement}
							onNewItemAddedToTabsElement={onNewItemAddedToTabsElement}
							tabBoxLayoutReferences={tabBoxLayoutReferences}
							nestedElementsRendered={nestedElementsRendered}
							onDragStartInTab={onDragStartInTab}
							isDroppable={checkPage()}
						/>
					) : (
						<div className='element-label-div'>
							<label
								style={{
									color: formAttributes?.labelColor
								}}
							>
								{renderLabel(formAttributes?.label, items[`${gridItem.i}`]?.type)}
							</label>
							<div className={'element-icons-box'}>
								<div className='element-icon-box'>{renderIcon(items[`${gridItem.i}`]?.type)}</div>
							</div>
						</div>
					)}
				</div>
			)
		})

	// update item position asn size in database
	const updateItem = async (item: IGridItem) => {
		// update formItemGeneral row and height
		const formItemData = { ...items[`${item.i}`]?.formItemGeneral }
		formItemData.row = item.h * LAYOUT_ROW_HEIGHT
		formItemData.col = item.w
		const formItem: IFormItemData = {
			x: item.x,
			y: item.y,
			i: item.i,
			h: item.h,
			w: item.w,
			formItemGeneral: formItemData
		}
		try {
			await patchReq(getApiEndpointPath(page, formId, items[`${item.i}`].id), undefined, formItem)
			dispatch({ type: FORM_EDITED, edited: true })
			dispatch({ type: LOAD, load: true })
		} catch (err: any) {
			// if some error occurred when user updating position of element, reload whole form items
			reload()
			// eslint-disable-next-line no-console
			console.error(err.message)
		} finally {
			dispatch({ type: LOAD, load: false })
		}
	}

	// get width of main builder layout for children layouts
	useLayoutEffect(() => {
		function updateSize() {
			const width = document.getElementById('main-builder-1')?.offsetWidth
			setMainContainerWidth(width || mainContainerWidth)
		}
		window.addEventListener('resize', updateSize)
		updateSize()
		return () => window.removeEventListener('resize', updateSize)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [])

	return (
		<div id={'main-builder-1'} className={'layout-border'}>
			<ResponsiveReactGridLayout
				className='layout main-builder'
				rowHeight={LAYOUT_ROW_HEIGHT}
				cols={{
					lg: 12,
					md: 12,
					sm: 12,
					xs: 12,
					xxs: 12
				}}
				margin={[10, 0]}
				layouts={{ lg: layouts }}
				droppingItem={{
					i: DROPPING_ELEMENT.KEY,
					h: placeholderHeight || DROPPING_ELEMENT.HEIGHT,
					w: placeholderWidth || DROPPING_ELEMENT.WIDTH
				}}
				onLayoutChange={onLayoutChange}
				onDrop={(layout: IGridItem[], layoutItem: IGridItem, _event: any) => onDrop(layout, layoutItem, _event)}
				measureBeforeMount={true}
				useCSSTransforms={true}
				removePlaceholder={removePlaceholder}
				// callback from lib when placeholder is removed
				placeholderRemoved={() => {
					setRemovePlaceholder(false)
				}}
				compactType={null}
				preventCollision={true}
				isDroppable={checkPage()}
				layoutKey={'main-builder-1'}
				onResizeStop={debounce((layout: IGridItem[], previousItem: IGridItem, item: IGridItem) => {
					resizeElement(layout, item)
					updateItem(item)
				}, PROTECTION_INTERVAL_FOR_SAVING_ELEMENT)}
				onDragStop={debounce((layout: IGridItem[], previousItem: IGridItem, item: IGridItem) => {
					updateItem(item)
				}, PROTECTION_INTERVAL_FOR_SAVING_ELEMENT)}
			>
				{renderLayoutElements()}
			</ResponsiveReactGridLayout>
		</div>
	)
}

export default React.memo(MainBuilder)
